import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import Brand from "./Brand.jsx";
import Footer from "./Footer.jsx";
import JurisdictionAckModal from "./JurisdictionAckModal.jsx";
import MobileDrawer from "./MobileDrawer.jsx";
import NetworkBanner from "./NetworkBanner.jsx";
import NotificationBell from "./NotificationBell.jsx";
import PausedDepositsBanner from "./PausedDepositsBanner.jsx";
import Sidebar from "./Sidebar.jsx";
import TopNav from "./TopNav.jsx";
import WalletConnect from "./WalletConnect.jsx";
import { useWallet } from "../context/WalletContext.jsx";
import { useJurisdictionAck } from "../hooks/useJurisdictionAck.js";

const PUBLIC_PATHS = new Set([
    "/",
    "/protocol-health",
    // FAQ is public reference content — it always renders on the full-bleed
    // public chrome (top navbar, no sidebar) whether or not a wallet is
    // connected, so a logged-in user reading it still gets the full page.
    "/faq",
    "/manifesto",
    "/404"
]);

/**
 * Shared chrome that adapts between two looks based on the current route:
 * - Public pages (Home, NotFound) get a top navbar with marketing links.
 * - Authenticated app pages get a persistent left sidebar plus a thin top
 *   bar that exposes the wallet connect / network banner. On small screens
 *   the sidebar collapses behind a hamburger -> MobileDrawer.
 *
 * Authenticated pages fall back to the public TopNav chrome whenever the
 * wallet is not connected, so the wallet-connect modal opens with the same
 * dark navbar visible as on Home.
 */
export default function Layout() {
    const location = useLocation();
    const { address } = useWallet();
    const isPublic = PUBLIC_PATHS.has(location.pathname) || !address;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { needsAck, save: saveAck, isSaving: isSavingAck } = useJurisdictionAck(address);

    useEffect(() => {
        setDrawerOpen(false);
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const ackModal = (
        <JurisdictionAckModal
            open={needsAck}
            isSaving={isSavingAck}
            onConfirm={(payload) => saveAck(payload, address)}
        />
    );

    if (isPublic) {
        return (
            <div className="flex min-h-screen flex-col bg-vb-bg">
                <div className="sticky top-0 z-40">
                    <TopNav />
                    <NetworkBanner />
                    <PausedDepositsBanner />
                </div>
                <main className="flex-1 w-full">
                    <Outlet />
                </main>
                <Footer />
                {ackModal}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f7f8f3] flex">
            <a href="#main-content" className="skip-link">Skip to main content</a>
            <Sidebar />
            <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
            {/* Sidebar is `position: fixed` (w-52 = 13rem), so add a
                left offset on md+ to keep the main column from sliding
                under it. */}
            <div className="flex min-h-screen min-w-0 flex-1 flex-col md:ml-52">
                <div className="sticky top-0 z-40">
                <header className="flex items-center gap-2 sm:gap-4 bg-[#f7f8f3] px-3 py-3 sm:px-8 sm:py-4">
                    <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#1D3E29] md:hidden"
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Open menu"
                    >
                        <svg
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            aria-hidden="true"
                        >
                            <line x1="3" y1="6" x2="17" y2="6" />
                            <line x1="3" y1="10" x2="17" y2="10" />
                            <line x1="3" y1="14" x2="17" y2="14" />
                        </svg>
                    </button>
                    <Link to="/" className="md:hidden" aria-label="GILDer home">
                        <Brand tone="dark" size="sm" />
                    </Link>

                    <div className="flex-1 md:hidden" aria-hidden="true" />

                    {(() => {
                        const crumbs = routeBreadcrumb(location.pathname);
                        // `routeBreadcrumb` may return null/[] to suppress
                        // the breadcrumb bar entirely (e.g., loan-detail
                        // pages own their header). Render a spacer so the
                        // flex layout stays balanced.
                        if (!crumbs || crumbs.length === 0) {
                            return <div className="hidden flex-1 md:block" aria-hidden="true" />;
                        }
                        return (
                            <nav
                                aria-label="Breadcrumb"
                                className="hidden flex-1 truncate font-manrope text-[14px] font-medium md:block"
                            >
                                <Link
                                    to="/dashboard"
                                    className="font-semibold text-[#10281b]/70 hover:text-[#10281b] hover:underline"
                                >
                                    GILDer Client Portal
                                </Link>
                                {crumbs.map((crumb, idx, arr) => {
                                    const isLast = idx === arr.length - 1;
                                    return (
                                        <span key={crumb.label}>
                                            <span className="mx-2 text-[#10281b]/40">/</span>
                                            {isLast ? (
                                                <span className="text-[#10281b]">{crumb.label}</span>
                                            ) : (
                                                <Link
                                                    to={crumb.to}
                                                    className="text-[#10281b]/70 hover:text-[#10281b] hover:underline"
                                                >
                                                    {crumb.label}
                                                </Link>
                                            )}
                                        </span>
                                    );
                                })}
                            </nav>
                        );
                    })()}

                    <Link
                        to="/deposit"
                        className="hidden md:inline-flex items-center gap-2 rounded-lg bg-[#10281b] px-6 py-1.5 font-manrope text-sm font-medium text-[#e6c985] hover:-translate-y-0.5 hover:shadow-md transition-all"
                    >
                        Deploy Capital
                        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="7 4 13 10 7 16" />
                        </svg>
                    </Link>

                    <NotificationBell />

                    {/* WalletConnect now renders its own dropdown chevron
                        + menu (Copy address / View on explorer / Disconnect)
                        when connected, so the inert chevron that used to
                        sit here is no longer needed. */}
                    <WalletConnect />
                </header>
                <NetworkBanner />
                <PausedDepositsBanner />
                </div>
                <main
                    id="main-content"
                    role="main"
                    className="flex-1 px-4 py-6 sm:px-8 sm:py-8"
                >
                    <Outlet />
                </main>
            </div>
            {ackModal}
        </div>
    );
}

/**
 * Maps the current path to an array of breadcrumb crumbs shown after
 * "GILDer Client Portal /" in the authenticated header.
 *
 * Each crumb is `{ label, to }`. The renderer treats the LAST crumb
 * as the current page (plain text, no link); every earlier crumb gets
 * rendered as a `<Link>` to its `to`.
 *
 * /term-deposits/:id is treated as the same surface as /term-deposits,
 * since the detail page is part of the same flow as the overview.
 */
function routeBreadcrumb(pathname) {
    if (pathname === "/loans/lending") {
        return [
            { label: "Loans Overview", to: "/loans" },
            { label: "Lending", to: "/loans/lending" }
        ];
    }
    // /loans/turbo route is hidden in V6.2 — no breadcrumb either.
    if (pathname.startsWith("/term-deposits")) {
        // On the detail route (/term-deposits/:id) add a second crumb
        // labelled "Term Deposit #000005" so the header surfaces which
        // position the user is viewing.
        const segments = pathname.split("/").filter(Boolean);
        const crumbs = [{ label: "My Term Deposits Overview", to: "/term-deposits" }];
        if (segments[1] === "recovery") {
            crumbs.push({ label: "Abandoned Recovery", to: pathname });
        } else if (segments.length > 1 && /^\d+$/.test(segments[1])) {
            const idPadded = String(segments[1]).padStart(6, "0");
            crumbs.push({
                label: `Term Deposit #${idPadded}`,
                to: pathname
            });
        }
        return crumbs;
    }
    if (pathname.startsWith("/deposit")) {
        return [{ label: "Open Term Deposit", to: "/deposit" }];
    }
    if (pathname.startsWith("/loans")) {
        // /loans/<numeric-id> → loan detail page; surface the loan id as
        // a second crumb so the header reads "... / Loans Overview / Loan #000001".
        const segments = pathname.split("/").filter(Boolean);
        const crumbs = [{ label: "Loans Overview", to: "/loans" }];
        if (segments.length > 1 && /^\d+$/.test(segments[1])) {
            const idPadded = String(segments[1]).padStart(6, "0");
            crumbs.push({ label: `Loan #${idPadded}`, to: pathname });
        }
        return crumbs;
    }
    if (pathname.startsWith("/reinvest")) {
        return [{ label: "Auto-Reinvest", to: "/reinvest" }];
    }
    if (pathname.startsWith("/my-tokens")) {
        return [{ label: "My Tokens", to: "/my-tokens" }];
    }
    if (pathname.startsWith("/activity")) {
        return [{ label: "Activity", to: "/activity" }];
    }
    if (pathname.startsWith("/settings")) {
        return [{ label: "Settings", to: "/settings" }];
    }
    if (pathname.startsWith("/support")) {
        return [{ label: "Help / Support", to: "/support" }];
    }
    if (pathname.startsWith("/marketplace")) {
        return [{ label: "Buy & Sell $GILD", to: "/marketplace" }];
    }
    return [{ label: "Dashboard", to: "/dashboard" }];
}
